import { Box, Grid2, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  useGetCurrentCampaignQuery,
  useRegenerateSuggestedTitlesMutation,
} from "../api";
import {
  LinksIcon,
  MingcutePdfIcon,
  PenIcon,
  StarsOutlineIcon,
} from "../components/images";
import {
  CustomizeDocumentationBlock,
  DeleteProductBtn,
  ProductDetailsModal,
  ProductGoToActionBlock,
  ProductNotes,
} from "../components/products";
import {
  ExpandedTextBlock,
  GradientButton,
  NavigationBreadcrumbs,
  ReuseTooltip,
} from "../components/ui";
import { CustomButton } from "../components/ui/CustomButton";
import { CommitteeRoleItem } from "../components/products/CommitteeRoleItem";
import { Statistics } from "../components/products/Statistics";
import { toast } from "react-toastify";
import { CustomToast } from "../components/ui/CustomToast";
import { getErrorMessage } from "../utils/getErrorMessage";
import { RestoreCampaignBtn } from "../components/ui/buttons";
import { AppRoutes } from "../types/enums";
import { QuestionMarkInCircleIcon } from "../components/images/QuestionMarkInCircleIcon";
import { useAppSelector, useNotFoundRedirect } from "../hooks";
import { SixSensBlock } from "../components/products/CustomizeDocumentationBlock/SixSensBlock";

const ProductPage = () => {
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const [isEditModal, setIsEditModal] = useState<boolean>(false);
  const { campaign_id } = useParams();

  const {
    data,
    refetch,
    error: currentCampaignError,
  } = useGetCurrentCampaignQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  useNotFoundRedirect({ error: currentCampaignError });

  const [mutate, { isError, isLoading, error }] =
    useRegenerateSuggestedTitlesMutation();

  useEffect(() => {
    if (isError && error) {
      toast.error(
        <CustomToast
          type="error"
          title="Failed to generate suggested titles."
          text={
            getErrorMessage(error).globalError ||
            "Something went wrong. Please try again"
          }
        />,
      );
    }
  }, [isError, error]);

  if (!data) return null;

  const {
    company_name,
    product_name,
    product_abstract,
    key_roles,
    source_pdf_names,
    source_url,
    is_demo,
    notes,
    notes_updated_at,
    total_accounts_analyzed,
  } = data;

  const breadcrumbs = [
    {
      title: product_name,
    },
  ];

  const submitChanges = () => {
    setIsEditModal(false);
    refetch();
  };

  return (
    <Stack sx={{ px: { sm: 8, md: 8, lg: 10, xl: 20 }, pt: 4 }} gap="40px">
      <Stack gap="24px" position="sticky">
        <NavigationBreadcrumbs breadcrumbs={breadcrumbs} />
        <Grid2 container spacing={4}>
          <Grid2 size={{ xs: 12, md: 7.7 }}>
            <Stack gap="24px" sx={{ pb: 6 }}>
              <Stack direction="row" alignItems="center" gap="16px">
                <Typography variant="h4" color="custom.textDarkBlue">
                  {product_name}
                </Typography>
                <Box
                  sx={{
                    minWidth: "8px",
                    minHeight: "8px",
                    backgroundColor: "#4C545B",
                    borderRadius: "50%",
                  }}
                />
                <Typography variant="h4" color="#797F84">
                  {company_name}
                </Typography>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack flexDirection="row" gap="8px">
                  <Typography
                    sx={{
                      fontSize: "18px",
                      lineHeight: "28px",
                      fontWeight: 500,
                      color: "#1F2932",
                    }}
                  >
                    Buying Committee Overview by Role
                  </Typography>
                  <ReuseTooltip
                    text={
                      "Buying committee is a group of people inside the target account who will be directly" +
                      " involved in the decision-making process for a deal involving your product, service," +
                      " or solution."
                    }
                  >
                    <QuestionMarkInCircleIcon />
                  </ReuseTooltip>
                </Stack>

                {isEditor && (
                  <ReuseTooltip
                    placement="top"
                    text={
                      "Get target profile suggestions based on the job titles, segmented by the role" +
                      " in the buying committee. Confirm the suggestion to add it to the campaign."
                    }
                  >
                    <CustomButton
                      onClick={() => mutate(campaign_id!)}
                      variant="outlined"
                      customColor="#FFFFFF"
                      textColor="#BD4D00"
                      customSize="fit"
                      type="button"
                      startIcon={<StarsOutlineIcon />}
                      loading={isLoading}
                      sx={{ height: "38px", borderColor: "#BD4D00" }}
                    >
                      Suggest Target Titles
                    </CustomButton>
                  </ReuseTooltip>
                )}
              </Stack>
              {total_accounts_analyzed === 0 && isEditor && (
                <ProductGoToActionBlock
                  title="Customize Targeting & Launch the Discovery"
                  texts={[
                    "Utilize title suggestions and segmentation to specify targeting. Customize buying committee" +
                      " composition using role switches. When ready, proceed to buying committee discovery in target" +
                      " accounts.",
                  ]}
                  btn={
                    <Link to={`${AppRoutes.ACCOUNTS_LIST}/${campaign_id}`}>
                      <GradientButton style={{ width: "100%" }}>
                        Proceed to Accounts
                      </GradientButton>
                    </Link>
                  }
                />
              )}

              {key_roles.map((role) => (
                <CommitteeRoleItem
                  campaign_id={campaign_id!}
                  role={role}
                  key={role.id}
                  isEditor={isEditor}
                />
              ))}
            </Stack>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 4.3 }}>
            <Stack
              gap="36px"
              position="sticky"
              sx={{
                padding: "12px",
                top: is_demo ? 110 : 74,
                height: `calc(100vh - 74px)`,
                scrollbarWidth: "none",
                overflow: "auto",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                pb: 6,
              }}
            >
              <Statistics data={data} campaign_id={campaign_id!} />
              <SixSensBlock campaign_id={campaign_id!} />
              <CustomizeDocumentationBlock
                campaign_id={campaign_id!}
                isEditor={isEditor}
              />
              <Stack gap="24px">
                <Stack gap="8px">
                  <Typography
                    variant="subtitle1"
                    color="custom.textDarkBlue"
                    sx={{ fontWeight: 500 }}
                  >
                    Product, Solution or Service Description
                  </Typography>
                  <ExpandedTextBlock text={product_abstract} />
                </Stack>

                {(source_url ||
                  (source_pdf_names && source_pdf_names.length > 0)) && (
                  <Stack gap="8px">
                    <Typography
                      variant="subtitle1"
                      color="custom.textDarkBlue"
                      sx={{ fontWeight: 500 }}
                    >
                      Sources
                    </Typography>
                    <Stack justifyContent="center" gap="10px">
                      {source_url && (
                        <Stack direction="row" alignItems="center" gap="10px">
                          <LinksIcon />
                          <Link
                            to={source_url}
                            target="_blank"
                            style={{ color: "#0069C4" }}
                          >
                            {source_url.length > 30
                              ? `${source_url.substring(0, 30)}...`
                              : source_url}
                          </Link>
                        </Stack>
                      )}

                      {source_pdf_names &&
                        source_pdf_names.length > 0 &&
                        source_pdf_names.map((name) => (
                          <Stack
                            key={name}
                            direction="row"
                            alignItems="center"
                            gap="10px"
                          >
                            <MingcutePdfIcon />
                            <Typography
                              variant="body1"
                              color="custom.textDarkBlue"
                              sx={{
                                display: "-webkit-box",
                                overflow: "hidden",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                textOverflow: "ellipsis",
                              }}
                            >
                              {name}
                            </Typography>
                          </Stack>
                        ))}
                    </Stack>
                  </Stack>
                )}

                <ProductNotes
                  campaign_id={campaign_id!}
                  notes={notes}
                  last_updated={notes_updated_at}
                />

                {isEditor && (
                  <Stack
                    direction="row"
                    gap="8px"
                    sx={{
                      flexWrap: {
                        "@media (max-width:1510px)": {
                          flexWrap: "wrap",
                        },
                        "@media (min-width:1511px)": {
                          flexWrap: "nowrap",
                        },
                      },
                    }}
                  >
                    <ReuseTooltip
                      placement="top"
                      hideChildren={false}
                      style={{ maxWidth: "100%", width: "100%" }}
                      text={
                        is_demo
                          ? "This feature is not available in the demo campaign"
                          : ""
                      }
                    >
                      <CustomButton
                        disabled={is_demo}
                        onClick={() => setIsEditModal(true)}
                        variant="outlined"
                        customColor="#FFFFFF"
                        textColor="#1D3E70"
                        fullWidth
                        type="button"
                        startIcon={<PenIcon />}
                        sx={{ height: "38px", borderColor: "#1D3E70" }}
                      >
                        Edit Data
                      </CustomButton>
                    </ReuseTooltip>

                    {is_demo ? (
                      <RestoreCampaignBtn campaign_id={campaign_id!} />
                    ) : (
                      <DeleteProductBtn campaign_id={campaign_id!} />
                    )}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Grid2>
        </Grid2>
      </Stack>

      {isEditModal && (
        <ProductDetailsModal
          initProductUrl={""}
          campaign_id={campaign_id!}
          isOpen={isEditModal}
          onClose={() => setIsEditModal(false)}
          onSubmit={submitChanges}
          company_name={company_name}
          product_name={product_name}
          product_abstract={product_abstract}
        />
      )}
    </Stack>
  );
};

export default ProductPage;
